import React, { useLayoutEffect, useRef, useState } from 'react';
import { setName } from './runeEngine';

/**
 * **룬 판 — 게임 룬 창 한 장.**
 *
 * 바탕(`board.webp`)은 사용자가 준 룬 창 그림(1964×801)에서 판 문양 여섯 개·아티팩트 둘·유물을 지우고,
 * 1번 판의 노란 테(고른 표시)는 4번 판을 위아래로 뒤집어 덮은 것이다(2026-10-13). 그 위에
 * 문양·고른 판 테·아티팩트·유물을 얹는다. 자리는 모두 그 그림에서 잰 값(그림 폭·높이에 대한 %)이다.
 * 1번 위, 2번 오른쪽 위, 3번 오른쪽 아래, 4번 아래, 5번 왼쪽 아래, 6번 왼쪽 위.
 *
 * 예전 별 틀(`frame-game.webp` + 유물 쪽 이음 그림 `frame-side.webp`)은 이걸로 바뀌었다.
 */

const IMG = '/img/runes';
export const BOARD_SRC = `${IMG}/board.webp`;
/** 그림 높이 / 폭 */
export const BOARD_RATIO = 801 / 1964;
export const glyphSrc = (file: string) => `${IMG}/gold/${file}.webp`;

/** 세트 번호 → 그림 이름 (SWARFARM 표기) */
const SET_FILE: Record<number, string> = {
    1: 'energy', 2: 'guard', 3: 'swift', 4: 'blade', 5: 'rage', 6: 'focus', 7: 'endure', 8: 'fatal',
    10: 'despair', 11: 'vampire', 13: 'violent', 14: 'nemesis', 15: 'will', 16: 'shield', 17: 'revenge',
    18: 'destroy', 19: 'fight', 20: 'determination', 21: 'enhance', 22: 'accuracy', 23: 'tolerance',
    24: 'seal', 25: 'intangible'
};

/** 룬돌 그림(칸 1~6) · 세트 문양 — 룬 고급 설정 결과의 작은 룬 칸이 쓴다(RuneAdvanced) */
export const runeStoneSrc = (slot: number) => `${IMG}/rune${slot}.png`;
export const setGlyphSrc = (set: number): string | null => (SET_FILE[set] ? glyphSrc(SET_FILE[set]) : null);

/** 판 가운데 (그림 폭·높이에 대한 %) — 지운 문양이 있던 자리 */
const SLOT_CENTER: Record<number, [number, number]> = {
    1: [50.03, 26.15], 2: [58.78, 38.2], 3: [58.45, 62.98], 4: [49.87, 76.59], 5: [41.17, 63.23], 6: [41.12, 37.52]
};

/**
 * **판의 윤곽** — 고른 판을 두르고, 판 전체를 누르는 자리. 그림에서 판의 회색 영역을 잡아 볼록 껍질을 씌우고
 * 꼭짓점을 대여섯 개로 줄인 값이다.
 */
const PLATE_OUTLINE: Record<number, Array<[number, number]>> = {
    1: [[54.0, 28.0], [51.3, 38.5], [48.0, 38.5], [47.3, 37.2], [45.0, 26.8], [49.5, 10.5]],
    2: [[64.4, 29.5], [60.5, 47.4], [55.3, 47.6], [54.9, 46.7], [53.4, 40.1], [55.9, 28.6]],
    3: [[64.4, 71.7], [56.0, 72.5], [53.4, 61.7], [55.2, 53.3], [60.4, 53.3]],
    4: [[54.0, 75.3], [49.8, 91.4], [49.3, 91.9], [45.0, 74.8], [47.5, 64.5], [51.3, 63.9]],
    5: [[44.0, 53.4], [45.7, 61.8], [43.2, 72.5], [34.8, 71.5], [38.5, 53.9]],
    6: [[45.8, 39.8], [43.9, 47.4], [38.6, 47.3], [34.9, 29.3], [43.1, 28.5], [44.5, 33.6]]
};

/**
 * **좁은 칸에서는 양옆 장식을 잘라 낸다**(`tight`) — 그림이 가로로 길어서(2.45:1) 좁은 칸에 다 넣으면 별이
 * 작아진다. 아티팩트(x 240~)와 유물(~x 1700)은 남기고 바깥 장식 테만 가운데 기준으로 같은 만큼 자른다.
 */
const FULL_W = 1964;
const TIGHT_X0 = 215;

/** 아티팩트 두 칸(위 속성 · 아래 유형)과 유물 자리 — 가운데 (%) 와 크기(그림 폭에 대한 비) */
const ART_AT: Array<[number, number]> = [[16.55, 33.46], [16.55, 63.92]];
const ART_SIZE = 0.1;
const RELIC_AT: [number, number] = [81.82, 53.06];
const RELIC_SIZE = 0.12;
/** 문양 크기 (그림 폭에 대한 비) — 지운 문양(57×85)이 들어가던 만큼 */
const GLYPH_SIZE = 0.056;

/**
 * 문양은 `gold/` 아래 것을 쓴다 — 검은 원본에 CSS 필터로 금색을 입히면 번지고 흐려서, 게임처럼
 * 선명한 금색 채움 + 어두운 갈색 테두리로 미리 그려 두었다(원본의 2배 크기, 4배로 그려 줄임).
 */

/**
 * 결과가 뜨기 전에 그림을 미리 받아 둔다.
 *
 * 별 틀·금색 문양 23종에 더해 **룬 창이 쓰는 룬돌 6장과 변환 아이콘**도 받는다 — 이것들은
 * 판을 눌러야 처음 쓰여서 빠져 있었고, 그래서 누를 때마다 한 박자 늦게 떴다. 받아 둔 Image
 * 객체는 모듈에 붙들어 둔다(버려지면 브라우저가 받다 말 수 있다). 두 번 불러도 한 번만 받는다.
 */
const preloaded: HTMLImageElement[] = [];
export function preloadRuneImages() {
    if (preloaded.length) return;
    const list = [
        BOARD_SRC,
        ...Object.values(SET_FILE).map(glyphSrc),
        ...[1, 2, 3, 4, 5, 6].map((n) => `${IMG}/rune${n}.png`),
        `${IMG}/gem.png`,
        ...[1, 2, 3, 4].map((n) => `/img/relics/tier${n}.webp`)
    ];
    for (const src of list) {
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
        preloaded.push(img);
    }
}

export default function RuneBoard({ slots, picked, onPick, artifacts, relic, width, tight = false }: {
    slots: Array<{ slot: number; set: number; ancient?: boolean }>;
    /** 눌러서 고른 판 — 금색 테로 두른다 */
    picked?: number | null;
    onPick?: (slot: number) => void;
    /** 아티팩트 두 칸 — 크기(px)를 받아 [속성, 유형] 을 그린다. 없으면(옵티마이저) 그 자리는 비운다 */
    artifacts?: (size: number) => [React.ReactNode, React.ReactNode];
    /** 유물 자리 — 크기(px)를 받아 그린다 */
    relic?: (size: number) => React.ReactNode;
    /** 폭 — 주지 않으면 칸 폭을 다 쓴다 */
    width?: number;
    /** 양옆 장식을 잘라 별을 키운다 (좁은 칸) */
    tight?: boolean;
}) {
    const bySlot = new Map(slots.map((s) => [s.slot, s]));
    // 아티팩트·유물·문양 크기를 그림 폭에 맞춘다 — 칸 폭을 잰다
    const box = useRef<HTMLDivElement | null>(null);
    const [w, setW] = useState(width || 300);
    useLayoutEffect(() => {
        if (width || !box.current) return;
        const el = box.current;
        const read = () => { if (el.clientWidth) setW(el.clientWidth); };
        read();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
        ro?.observe(el);
        return () => ro?.disconnect();
    }, [width]);
    // 보이는 폭(그림 px) — 잘라 내면 그만큼 확대된다. 자리(%)는 보이는 폭 기준으로 옮긴다
    const x0 = tight ? TIGHT_X0 : 0;
    const cw = FULL_W - 2 * x0;
    const zoom = FULL_W / cw;
    const nx = (x: number) => +(((x / 100) * FULL_W - x0) / cw * 100).toFixed(2);
    const at = ([x, y]: [number, number]): React.CSSProperties => ({ position: 'absolute', left: `${nx(x)}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' });
    const outline = (slot: number) => (PLATE_OUTLINE[slot] || []).map(([x, y]) => `${nx(x)},${y}`).join(' ');
    const arts = artifacts ? artifacts(Math.round(w * zoom * ART_SIZE)) : null;
    const glyph = Math.round(w * zoom * GLYPH_SIZE);

    return (
        <div
            ref={box} role="img" aria-label="룬 배치"
            style={{
                position: 'relative', width: width || '100%', aspectRatio: `${cw} / 801`, flexShrink: 0,
                backgroundImage: `url(${BOARD_SRC})`, backgroundSize: `${(zoom * 100).toFixed(2)}% 100%`, backgroundPosition: 'center',
                borderRadius: 14, overflow: 'hidden',
                border: '1px solid #7a4a22'
            }}
        >
            {/* 문양 */}
            {[1, 2, 3, 4, 5, 6].map((n) => {
                const s = bySlot.get(n);
                if (!s) return null;
                const file = SET_FILE[s.set];
                return (
                    <span key={n} style={{ ...at(SLOT_CENTER[n]), width: glyph, height: glyph, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        {file ? (
                            <img loading="lazy" src={glyphSrc(file)} alt={setName(s.set)}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.45))' }} />
                        ) : (
                            <span style={{ color: '#efa024', fontWeight: 900, fontSize: glyph * 0.6 }}>{setName(s.set).slice(0, 1)}</span>
                        )}
                    </span>
                );
            })}

            {/* 판 — 누르는 자리(판 모양 그대로)와 고른 판의 금색 테(깜박인다) */}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                <style>{`@keyframes rune-pick-blink { 0%, 100% { opacity: 1 } 50% { opacity: 0.15 } }`}</style>
                {[1, 2, 3, 4, 5, 6].map((n) => {
                    const s = bySlot.get(n);
                    if (!s) return null;
                    const label = `${n}번 룬 · ${setName(s.set)}${s.ancient ? ' (고대)' : ''}`;
                    return (
                        <polygon
                            key={n} points={outline(n)} fill="transparent"
                            onClick={onPick ? () => onPick(n) : undefined}
                            style={{ cursor: onPick ? 'pointer' : 'default' }}
                        ><title>{label}</title></polygon>
                    );
                })}
                {picked && PLATE_OUTLINE[picked] && (
                    <polygon
                        points={outline(picked)} fill="none" stroke="#ffd75e" strokeWidth={3} strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        style={{ pointerEvents: 'none', animation: 'rune-pick-blink 0.9s ease-in-out infinite', filter: 'drop-shadow(0 0 3px rgba(255, 200, 80, 0.9))' }}
                    />
                )}
            </svg>

            {/* 아티팩트 둘 · 유물 */}
            {arts && arts.map((node, i) => <div key={i} style={at(ART_AT[i])}>{node}</div>)}
            {relic && <div style={at(RELIC_AT)}>{relic(Math.round(w * zoom * RELIC_SIZE))}</div>}
        </div>
    );
}
